import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Clock, MapPin, Star, Truck, ShoppingBag } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

const HERO_BG = "https://images.unsplash.com/photo-1569718212165-3a8278d5f624?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000";
const BO_BUN = "https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?crop=entropy&cs=srgb&fm=jpg&w=1200";
const STIR_FRY_NOODLES = "https://images.unsplash.com/photo-1585032226651-759b368d7246?crop=entropy&cs=srgb&fm=jpg&w=1200";
const SPRING_ROLLS = "https://images.pexels.com/photos/36752868/pexels-photo-36752868.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940";

const Home = () => {
  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Header />

      {/* HERO */}
      <section className="relative w-full h-[88vh] min-h-[600px] overflow-hidden">
        <img src={HERO_BG} alt="Quang restaurant" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />

        <div className="relative h-full max-w-7xl mx-auto px-5 md:px-8 lg:px-12 flex flex-col justify-end pb-16 md:pb-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-5 bg-white/15 backdrop-blur-md border border-white/20 rounded-full px-3.5 py-1.5">
              <span className="w-2 h-2 rounded-full bg-[#2A9D8F] animate-pulse" />
              <span className="text-xs uppercase tracking-[0.2em] text-white font-semibold">Ouvert · 10h — 22h</span>
            </div>

            <h1
              className="text-white font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
              style={{ fontFamily: "'Cabinet Grotesk', 'Manrope', sans-serif" }}
              data-testid="hero-title"
            >
              Le vrai goût<br />du <span className="text-[#D90429]">Vietnam</span>,<br />à Lausanne.
            </h1>

            <p className="text-white/85 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
              Phở fumants, Bò Bún généreux et nems croustillants — préparés à la minute par Quang.
              À emporter ou livrés en moins de 35 minutes.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/commander" data-testid="hero-cta-order">
                <Button className="bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-12 px-7 text-base font-bold tracking-wide w-full sm:w-auto">
                  Commander maintenant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <a href="#menu-preview">
                <Button variant="outline" className="bg-white/10 hover:bg-white/20 backdrop-blur-md border-white/30 text-white rounded-md h-12 px-7 text-base font-medium w-full sm:w-auto">
                  Voir le menu
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-6 mt-10 text-white/80 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#D90429]" />
                <span>15 min de préparation</span>
              </div>
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-[#D90429]" />
                <span>Livraisons sur Lausanne</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-[#E9C46A] fill-[#E9C46A]" />
                <span>4.7 / 5 (615 avis)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES STRIP */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: ShoppingBag, label: "À emporter", text: "Prêt en 15 min, à récupérer sur place" },
            { icon: Truck, label: "Livraison", text: "Lausanne et environs — frais selon code postal" },
            { icon: MapPin, label: "Sur place", text: "Rue Pré-du-Marché 15 · 1004 Lausanne" },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-md bg-[#D90429] flex items-center justify-center flex-shrink-0">
                <f.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-neutral-400 font-bold mb-1">{f.label}</p>
                <p className="text-sm text-neutral-200">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SIGNATURE DISHES */}
      <section id="menu-preview" className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D90429] font-bold mb-3">Nos signatures</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Plats faits maison,<br />recettes de famille.
            </h2>
          </div>
          <Link to="/commander">
            <Button variant="outline" className="border-black hover:bg-black hover:text-white rounded-md h-11 px-6 font-semibold" data-testid="see-full-menu-btn">
              Voir tout le menu
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {[
            { name: "Bò Bún bœuf", desc: "Vermicelles de riz, bœuf sauté à la citronnelle, nems, herbes fraîches, cacahuètes et sauce maison", price: "17.50", img: BO_BUN },
            { name: "Nouilles sautées poulet", desc: "Nouilles wok au poulet, légumes croquants, sauce soja parfumée — un classique généreux", price: "16.50", img: STIR_FRY_NOODLES },
            { name: "Rouleaux de printemps", desc: "Crevette, porc, vermicelles, salade, menthe — sauce maison à la cacahuète", price: "7.00", img: SPRING_ROLLS },
          ].map((d, i) => (
            <div key={i} className="group relative bg-white border border-neutral-200 rounded-xl overflow-hidden hover:shadow-lg transition-all">
              <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                <img src={d.img} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="font-black text-xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{d.name}</h3>
                  <span className="font-bold text-base whitespace-nowrap">{d.price} CHF</span>
                </div>
                <p className="text-sm text-neutral-500 leading-relaxed">{d.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT STRIP */}
      <section className="bg-white border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] rounded-xl overflow-hidden">
              <img src={SPRING_ROLLS} alt="Cuisine vietnamienne" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D90429] font-bold mb-3">Notre histoire</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Une cuisine sincère,<br />portée par la tradition.
            </h2>
            <p className="text-base text-neutral-700 leading-relaxed mb-4">
              Chez Quang, chaque plat raconte une histoire. Nos bouillons mijotent pendant des heures,
              nos herbes sont fraîches du marché, et nos recettes traversent les générations.
            </p>

            <div className="grid grid-cols-3 gap-4 mt-10">
              {[
                { n: "12+", l: "Ans à Lausanne" },
                { n: "30+", l: "Plats au menu" },
                { n: "4.7★", l: "Note moyenne" },
              ].map((s, i) => (
                <div key={i} className="border-t-2 border-[#D90429] pt-4">
                  <p className="text-3xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.n}</p>
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#D90429] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-20 md:py-24 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Faim ? On s&apos;occupe du reste.
          </h2>
          <p className="text-white/90 text-base sm:text-lg mb-8 max-w-xl mx-auto">
            Commandez en quelques clics — à emporter ou livré chez vous en moins de 35 minutes.
          </p>
          <Link to="/commander">
            <Button className="bg-white hover:bg-neutral-100 text-[#D90429] rounded-md h-12 px-8 text-base font-black tracking-wide" data-testid="cta-order-now">
              Commander maintenant
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
